// First-party port of @missionsquad/browserai's audio capture helpers (audio/recorder.ts,
// audio/decode.ts, audio/pcm.ts — same repo, our package). Recording and WAV encoding must not
// depend on the browserai SDK import: the SDK is only the *local* transcription engine, and the
// server-side STT fallback has to keep working even when that chunk fails to load. Keep this in
// sync with the SDK's MicRecorder if it changes.

/** Sample rate every STT consumer expects (Whisper-style 16 kHz mono). */
export const AUDIO_SR = 16000

/** Microphone acquisition failed. `permissionDenied` distinguishes user/browser denial. */
export class MicrophoneError extends Error {
  readonly permissionDenied: boolean

  constructor(cause: unknown) {
    const detail = cause instanceof Error ? cause.message : String(cause)
    super(`Microphone error: ${detail}`)
    this.name = 'MicrophoneError'
    this.permissionDenied =
      cause instanceof DOMException && (cause.name === 'NotAllowedError' || cause.name === 'SecurityError')
  }
}

/**
 * Decode an audio file (ArrayBuffer) to a 16 kHz mono Float32Array, matching Transformers.js
 * `read_audio` output — so the same PCM feeds either the browser-local model or the server relay.
 * Browser main-thread only: uses `AudioContext`/`OfflineAudioContext`.
 */
export async function decodeAudioTo16kMono(buffer: ArrayBuffer): Promise<Float32Array> {
  const ctx = new AudioContext()
  // Close the context even when decode rejects (e.g. a header-only blob from an instant start/stop).
  const decoded = await ctx.decodeAudioData(buffer).finally(() => ctx.close().catch(() => undefined))
  const offline = new OfflineAudioContext(1, Math.ceil(decoded.duration * AUDIO_SR), AUDIO_SR)
  const source = offline.createBufferSource()
  source.buffer = decoded
  source.connect(offline.destination)
  source.start()
  const rendered = await offline.startRendering()
  return rendered.getChannelData(0)
}

/** Encode a mono Float32 PCM buffer as a 16-bit PCM WAV Blob (`type: 'audio/wav'`). */
export function encodeWavBlob(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2)
  const view = new DataView(buffer)
  const writeStr = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i))
  }
  writeStr(0, 'RIFF')
  view.setUint32(4, 36 + samples.length * 2, true)
  writeStr(8, 'WAVE')
  writeStr(12, 'fmt ')
  view.setUint32(16, 16, true) // PCM fmt chunk size
  view.setUint16(20, 1, true) // PCM format
  view.setUint16(22, 1, true) // mono
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true) // byte rate
  view.setUint16(32, 2, true) // block align
  view.setUint16(34, 16, true) // bits per sample
  writeStr(36, 'data')
  view.setUint32(40, samples.length * 2, true)
  let offset = 44
  for (let i = 0; i < samples.length; i += 1) {
    const clamped = Math.max(-1, Math.min(1, samples[i]))
    view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true)
    offset += 2
  }
  return new Blob([buffer], { type: 'audio/wav' })
}

export type MicRecorderOptions = {
  /** Called once per take after the mic is live — attach analyzers/visualizers here. */
  onStreamReady?: (graph: { context: AudioContext; source: MediaStreamAudioSourceNode; stream: MediaStream }) => void
  /** Called during teardown, before the context closes — dispose whatever onStreamReady created. */
  onStreamEnded?: () => void
}

type ActiveTake = {
  ctx: AudioContext
  stream: MediaStream
  recorder: MediaRecorder
  chunks: Blob[]
}

/**
 * Press-to-talk microphone recorder: getUserMedia → MediaRecorder with no timeslice (one blob at
 * stop) → 16 kHz mono PCM. Hardened lifecycle:
 * - a synchronous pending claim so a double-press cannot start two recorders;
 * - an inactive-recorder guard (the recorder auto-stops when its track ends — device unplugged,
 *   permission revoked — and `stop()` would then be a silent no-op whose `onstop` never fires);
 * - try/finally teardown that always releases the mic tracks and closes the AudioContext.
 */
export class MicRecorder {
  #options: MicRecorderOptions
  #pending = false
  #abortPending = false
  #take: ActiveTake | null = null

  constructor(options: MicRecorderOptions = {}) {
    this.#options = options
  }

  /** Whether a take is currently being captured (true between a successful start() and stop()). */
  get recording(): boolean {
    return this.#take !== null
  }

  #streamEnded(): void {
    try {
      this.#options.onStreamEnded?.()
    } catch {
      // A throwing host disposal hook must not interrupt mic/context teardown.
    }
  }

  /**
   * Start capturing. No-op (returns false) when already recording or a start is mid-flight.
   * @throws {MicrophoneError} when the mic cannot be acquired (`permissionDenied` set for denials).
   */
  async start(): Promise<boolean> {
    if (this.#pending || this.#take) return false
    this.#pending = true // claimed synchronously before the getUserMedia await (double-press guard)
    let stream: MediaStream | null = null
    let ctx: AudioContext | null = null
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      if (this.#abortPending) {
        // cancel() ran while the permission prompt / acquisition was pending — release and bail.
        this.#abortPending = false
        stream.getTracks().forEach((track) => track.stop())
        return false
      }
      ctx = new AudioContext()
      const source = ctx.createMediaStreamSource(stream)
      try {
        this.#options.onStreamReady?.({ context: ctx, source, stream })
      } catch {
        // A throwing host visualization hook must not abort the take.
      }
      const recorder = new MediaRecorder(stream)
      const chunks: Blob[] = []
      recorder.ondataavailable = (event) => chunks.push(event.data)
      recorder.start()
      this.#take = { ctx, stream, recorder, chunks }
      return true
    } catch (error) {
      // Release anything acquired before the failure — otherwise the mic indicator stays lit.
      stream?.getTracks().forEach((track) => track.stop())
      this.#streamEnded()
      if (ctx) void ctx.close().catch(() => undefined)
      throw new MicrophoneError(error)
    } finally {
      this.#pending = false
    }
  }

  /**
   * Stop the take and decode it to 16 kHz mono PCM. Returns null when nothing was recorded (no
   * take in flight, or an empty capture). Always releases the mic, even if stopping throws.
   */
  async stop(): Promise<Float32Array | null> {
    const take = this.#take
    this.#take = null // claim synchronously so a re-entrant stop can't double-clean
    if (!take) return null
    const { recorder, stream, ctx, chunks } = take
    try {
      await new Promise<void>((resolve) => {
        // The recorder auto-stops when its track ends (device unplugged, permission revoked). stop()
        // would then be a silent no-op and onstop would never fire — resolve immediately instead.
        if (recorder.state === 'inactive') {
          resolve()
          return
        }
        recorder.onstop = () => resolve()
        recorder.stop()
      })
    } finally {
      // Always release the mic and context — even if stop() threw (legacy InvalidStateError).
      stream.getTracks().forEach((track) => track.stop())
      this.#streamEnded()
      await ctx.close().catch(() => undefined)
    }
    const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' })
    if (blob.size === 0) return null
    return decodeAudioTo16kMono(await blob.arrayBuffer())
  }

  /** Abandon a take without decoding it (releases the mic; the audio is discarded). Also aborts a start() still awaiting the permission prompt. */
  async cancel(): Promise<void> {
    if (this.#pending && !this.#take) this.#abortPending = true // start() releases the mic when getUserMedia resolves
    const take = this.#take
    this.#take = null
    if (!take) return
    const { recorder, stream, ctx } = take
    try {
      await new Promise<void>((resolve) => {
        if (recorder.state === 'inactive') {
          resolve()
          return
        }
        recorder.onstop = () => resolve()
        recorder.stop()
      })
    } finally {
      stream.getTracks().forEach((track) => track.stop())
      this.#streamEnded()
      await ctx.close().catch(() => undefined)
    }
  }
}
